import { memo, type ReactNode, useEffect } from 'react';
import { useResizeDetector } from 'react-resize-detector';

import { useKanbanOffsetContext } from '~/components/v2/components/shared/kanban/KanbanOffsetContextProvider';
import { useTableHeaderPositionContext } from '~/components/v2/components/shared/table/TableHeaderPositionContextProvider';

export type PublicBoardTopbarProps = {
  /**
   * The breadcrumb trail on the left. Supplied by the hosting area so this shell carries no knowledge
   * of whether it sits in a standalone board share or inside a collection: the board page passes
   * board-only crumbs (PublicBoardTopbarBreadcrumbs), the collection board view passes the
   * collection-prefixed trail (PublicShowcaseBoardBreadcrumbs).
   */
  breadcrumbs: ReactNode;
  /**
   * Trailing content on the right (the board share's "Save all" download menu). Supplied only by the
   * standalone board page — the collection host supplies none, because "Save all" is a viewer action
   * that reads PublicRootBoardProvider (never mounted under the collection tree). Composition here
   * replaces the old `usePublicViewerHost()` "am I in the editor?" check.
   */
  trailingContent?: ReactNode;
};

export const PublicBoardTopbar = memo(({ breadcrumbs, trailingContent }: PublicBoardTopbarProps) => {
  const { ref, height } = useResizeDetector({
    handleWidth: false,
  });
  const { setTableHeaderTopPosition } = useTableHeaderPositionContext();
  const { setKanbanOffset } = useKanbanOffsetContext();
  useEffect(() => {
    if (height) {
      setTableHeaderTopPosition(height);
      setKanbanOffset(height);
    }
  }, [height, setTableHeaderTopPosition, setKanbanOffset]);

  return (
    // shrink-0: this bar is a child of the board page's flex-column scroll container, and without
    // it the browser compresses the fixed h-14 row once the board's content overflows the viewport
    // (the squish only appears after covers/items load — the page looks right on first paint).
    <div
      className="sticky top-0 z-30 flex h-14 shrink-0 items-center justify-between ads:bg-base bg-gray-1 px-4"
      ref={ref}
    >
      {breadcrumbs}
      {trailingContent}
    </div>
  );
});

PublicBoardTopbar.displayName = 'PublicBoardTopbar';
